/**
 * AOEffect - unified ambient occlusion for WebGL (SSAO) and WebGPU (GTAO via builtinAOContext).
 */

import { BlendFunction, NormalPass, SSAOEffect } from 'postprocessing';
import * as THREE from 'three';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { builtinAOContext, clamp, colorToDirection, float, sample, screenUV, select, vec4 } from 'three/tsl';

import { type EffectContext, IPostProcessEffect, type WebGLEffectData, type WebGPUComposeContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { type AOEffectConfig, DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';

import type GTAONode from 'three/addons/tsl/display/GTAONode.js';

/**
 * Unified AO (Ambient Occlusion) effect for both WebGL and WebGPU.
 *
 * WebGL: Uses SSAO (Screen Space Ambient Occlusion) from postprocessing library
 * WebGPU: Uses GTAO (Ground Truth Ambient Occlusion) from TSL via builtinAOContext
 */
export class AOEffect extends IPostProcessEffect<AOEffectConfig> {
  override readonly type = PostProcessPass.AO;
  override readonly order = PostProcessRenderOrder.AO;

  private normalPass: NormalPass | null = null;
  private effectNodes = new Map<string, WebGPUEffectNode>();

  override getDefaultConfig(): AOEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.AO] };
  }

  /** Get WebGL-specific defaults (SSAO uses different radius scale) */
  private getWebGLDefaults(): Partial<AOEffectConfig> {
    return {
      ssaoRadius: 0.1,
      ssaoSamples: 9,
      ssaoStrength: 1.33,
      resolutionScale: 1.0,
      bias: 0.025,
      fade: 0.01,
      luminanceInfluence: 0.7,
      minRadiusScale: 0.33,
      depthAwareUpsampling: true,
      worldDistanceThreshold: 0.02,
      worldDistanceFalloff: 0.0025,
      worldProximityThreshold: 0.0003,
      worldProximityFalloff: 0.0001,
    };
  }

  /** Get WebGPU-specific defaults (GTAO uses world-space radius) */
  private getWebGPUDefaults(): Partial<AOEffectConfig> {
    return {
      gtaoRadius: 0.25,
      gtaoSamples: 16,
      gtaoStrength: 1.0,
      resolutionScale: 1.0,
      bias: 0.025,
      thickness: 1.0,
      distanceExponent: 1.0,
      distanceFallOff: 1.0,
    };
  }

  // ============= WebGL Implementation =============

  override createWebGLEffect(config: AOEffectConfig, context: EffectContext): WebGLEffectData {
    const defaults = this.getWebGLDefaults();

    // Create NormalPass for SSAO
    this.normalPass = new NormalPass(context.scene, context.camera, {
      resolutionScale: config.resolutionScale ?? defaults.resolutionScale,
    });

    const effect = new SSAOEffect(context.camera, this.normalPass.texture, {
      blendFunction: BlendFunction.MULTIPLY,
      samples: config.ssaoSamples ?? defaults.ssaoSamples,
      rings: 7,
      radius: config.ssaoRadius ?? defaults.ssaoRadius,
      intensity: config.ssaoStrength ?? defaults.ssaoStrength,
      bias: config.bias ?? defaults.bias,
      fade: config.fade ?? defaults.fade,
      luminanceInfluence: config.luminanceInfluence ?? defaults.luminanceInfluence,
      minRadiusScale: config.minRadiusScale ?? defaults.minRadiusScale,
      depthAwareUpsampling: config.depthAwareUpsampling ?? defaults.depthAwareUpsampling,
      // Map our config names to postprocessing library parameter names
      distanceThreshold: config.worldDistanceThreshold ?? defaults.worldDistanceThreshold,
      distanceFalloff: config.worldDistanceFalloff ?? defaults.worldDistanceFalloff,
      rangeThreshold: config.worldProximityThreshold ?? defaults.worldProximityThreshold,
      rangeFalloff: config.worldProximityFalloff ?? defaults.worldProximityFalloff,
      resolutionScale: config.resolutionScale ?? defaults.resolutionScale,
    });

    // Apply color if specified
    if (config.color !== undefined && config.color !== null) {
      effect.color = new THREE.Color(config.color);
    }

    return {
      effects: [effect],
      passes: [this.normalPass]
    };
  }

  override applyWebGLConfig(effectData: WebGLEffectData, config: AOEffectConfig): void {
    const defaults = this.getWebGLDefaults();
    const ssaoEffect = effectData.effects[0] as SSAOEffect;

    if (ssaoEffect) {
      const material = ssaoEffect.ssaoMaterial;
      if (config.ssaoSamples !== undefined) material.samples = config.ssaoSamples;
      if (config.ssaoRadius !== undefined) material.radius = config.ssaoRadius;
      if (config.ssaoStrength !== undefined) ssaoEffect.intensity = config.ssaoStrength;
      if (config.bias !== undefined) material.bias = config.bias;
      if (config.fade !== undefined) material.fade = config.fade;
      if (config.luminanceInfluence !== undefined) (ssaoEffect as any).luminanceInfluence = config.luminanceInfluence;
      if (config.minRadiusScale !== undefined) material.minRadiusScale = config.minRadiusScale;
      if (config.depthAwareUpsampling !== undefined) ssaoEffect.depthAwareUpsampling = config.depthAwareUpsampling;
      if (config.worldDistanceThreshold !== undefined) material.worldDistanceThreshold = config.worldDistanceThreshold;
      if (config.worldDistanceFalloff !== undefined) material.worldDistanceFalloff = config.worldDistanceFalloff;
      if (config.worldProximityThreshold !== undefined) material.worldProximityThreshold = config.worldProximityThreshold;
      if (config.worldProximityFalloff !== undefined) material.worldProximityFalloff = config.worldProximityFalloff;
      if (config.color !== undefined) {
        ssaoEffect.color = config.color === null ? null! : new THREE.Color(config.color);
      }
    }
  }

  // ============= WebGPU Implementation =============

  override createWebGPUNode(config: AOEffectConfig, context: EffectContext): WebGPUEffectNode {
    const defaults = this.getWebGPUDefaults();

    // Create AO node (inputs will be set in applySceneContext)
    const aoNode = ao(
      null as any, // depth texture
      null as any, // normal texture
      context.camera
    );

    // Configure AO parameters
    aoNode.radius.value = config.gtaoRadius ?? defaults.gtaoRadius!;
    aoNode.scale.value = config.gtaoStrength ?? defaults.gtaoStrength!;
    aoNode.thickness.value = config.thickness ?? defaults.thickness!;
    aoNode.samples.value = config.gtaoSamples ?? defaults.gtaoSamples!;
    aoNode.distanceExponent.value = config.distanceExponent ?? defaults.distanceExponent!;
    aoNode.distanceFallOff.value = config.distanceFallOff ?? defaults.distanceFallOff!;
    aoNode.resolutionScale = config.resolutionScale ?? defaults.resolutionScale!;

    const effectNode = {
      node: aoNode,
      // Request scene normal MRT so AO uses the same pass depth/normal as final shading.
      needsMRT: true,
      mrtOutputs: ['normal'],
      // No compose - AO is applied via builtinAOContext
    };

    // Store for later access in applySceneContext
    this.effectNodes.set(this.type, effectNode);

    return effectNode;
  }

  override applyWebGPUConfig(node: WebGPUEffectNode, config: AOEffectConfig): void {
    const defaults = this.getWebGPUDefaults();
    const aoNode = node.node as GTAONode;

    if (aoNode) {
      aoNode.radius.value = config.gtaoRadius ?? defaults.gtaoRadius!;
      aoNode.scale.value = config.gtaoStrength ?? defaults.gtaoStrength!;
      aoNode.thickness.value = config.thickness ?? defaults.thickness!;
      aoNode.samples.value = config.gtaoSamples ?? defaults.gtaoSamples!;
      aoNode.distanceExponent.value = config.distanceExponent ?? defaults.distanceExponent!;
      aoNode.distanceFallOff.value = config.distanceFallOff ?? defaults.distanceFallOff!;
    }
  }

  override applySceneContext(scenePass: any, _config: AOEffectConfig, context: WebGPUComposeContext): void {
    const aoNode = this.effectNodes.get(this.type)?.node as GTAONode;
    if (!aoNode) return;

    const sceneNormalTexture = context.getMRTTexture('normal') as any;
    const sceneNormal = sceneNormalTexture
      ? sample((uv: any) => colorToDirection(sceneNormalTexture.sample(uv)))
      : null;

    // Build a masked depth node that returns sky-depth (1.0) at pixels where the normal
    // MRT alpha < 0.5 (transparent/particle pixels write alpha=0; geometry writes alpha=1).
    // GTAO's built-in "depth >= 1.0 → discard" then skips those pixels entirely:
    //   • AO texture at particle pixels = 1.0 (white = no occlusion from renderer clear).
    //   • During neighbour depth-sampling for wall pixels, samples landing on transparent
    //     positions get sky-depth → |viewDelta.z| >> thickness → thickness test fails →
    //     no cosHorizon update → no incorrect occlusion on adjacent wall fragments.
    const maskedDepthNode = sceneNormalTexture
      ? sample((uv: any) => {
        const normalSample = sceneNormalTexture.sample(uv);
        const depthValue = (context.depthNode as any).sample(uv).r;
        return vec4(select(normalSample.a.lessThan(0.5), float(1.0), depthValue), 0, 0, 0);
      })
      : context.depthNode;

    aoNode.depthNode = maskedDepthNode;
    aoNode.normalNode = sceneNormal as any;

    // Clamp AO to avoid invalid samples creating full-black occlusion spikes.
    const aoPassOutput = aoNode.getTextureNode();
    scenePass.contextNode = builtinAOContext(clamp(aoPassOutput.sample(screenUV).r, 0.0, 1.0));
  }

  override requiresRebuild(oldConfig: AOEffectConfig, newConfig: AOEffectConfig): boolean {
    // Rebuild if enabled state changes or resolution scale changes (affects MRT setup)
    return (
      oldConfig.enabled !== newConfig.enabled ||
      oldConfig.resolutionScale !== newConfig.resolutionScale
    );
  }

  override dispose(): void {
    this.normalPass?.dispose();
    this.normalPass = null;
  }
}
